<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div v-if="showModal" class="modal-overlay" v-on:click.self="closeModal">
        <transition name="slide-in">
          <div v-if="showModal" class="modal-panel">
            <header class="modal-head">
              <div>
                <div class="modal-eyebrow">
                  <span class="status-dot"></span>
                  {{ isEdit ? "Edit account" : "New account" }}
                </div>
                <h2>{{ isEdit ? "Edit Bank Account" : "Add Bank Account" }}</h2>
                <p>The bank account your organization receives payments into.</p>
              </div>
              <button type="button" class="icon-button" v-on:click="closeModal" aria-label="Close">
                <font-awesome-icon icon="fa-solid fa-xmark" />
              </button>
            </header>

            <Form class="modal-form" v-on:submit="saveBankAccount">
              <div class="modal-body">
                <section class="form-section">
                  <h3>Account Details</h3>
                  <div class="form-grid">
                    <label class="field full">
                      <span>Label <small>(optional)</small></span>
                      <Field
                        type="text"
                        name="name"
                        v-model.trim="bankAccountObj.name"
                        placeholder="e.g. Main CBE account"
                        :rules="{ max: [100] }"
                      />
                      <ErrorMessage name="name" class="form-text text-danger" />
                    </label>

                    <label class="field full">
                      <span>Bank</span>
                      <select v-model="bankAccountObj.bank">
                        <option :value="null" disabled>Select bank</option>
                        <option v-for="bank in banks" :key="bank.value" :value="bank.value">{{ bank.label }}</option>
                      </select>
                      <span v-if="bankError" class="form-text text-danger">{{ bankError }}</span>
                    </label>

                    <label class="field full">
                      <span>Account Name <small>(optional)</small></span>
                      <Field
                        type="text"
                        name="accountName"
                        v-model.trim="bankAccountObj.accountName"
                        placeholder="Name on the account"
                        :rules="{ max: [100] }"
                      />
                      <ErrorMessage name="accountName" class="form-text text-danger" />
                    </label>

                    <label class="field full">
                      <span>Account Number</span>
                      <Field
                        type="text"
                        name="accountNumber"
                        v-model.trim="bankAccountObj.accountNumber"
                        inputmode="numeric"
                        placeholder="1000123456789"
                        :rules="{ required: ['Account Number'], digits: true, max: [50] }"
                      />
                      <ErrorMessage name="accountNumber" class="form-text text-danger" />
                    </label>
                  </div>
                </section>
              </div>

              <footer class="modal-foot">
                <button type="button" class="ghost-button" v-on:click="closeModal">Cancel</button>
                <button
                  type="submit"
                  class="primary-button"
                  :disabled="bankAccountIsLoading"
                >
                  <span v-if="!bankAccountIsLoading">{{ isEdit ? "Save Changes" : "Add Account" }}</span>
                  <span v-else>Saving...</span>
                </button>
              </footer>
            </Form>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { Field, Form, ErrorMessage, defineRule } from "vee-validate";
import { EDIT_BANK_ACCOUNT, REGISTER_BANK_ACCOUNT } from "@/store/action_types";
import { BANKS, BANK_LABELS } from "@/utils/enums";
import { TOAST_GLOBAL_CONFIG } from "@/utils/const";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

library.add(faXmark);

defineRule("required", (value, [fieldName = "Field"]) => {
  if (value === null || value === undefined || String(value).trim().length === 0) {
    return `${fieldName} is required`;
  }
  return true;
});
defineRule("digits", (value) => {
  if (value && !/^[0-9]+$/.test(String(value))) {
    return "Account number may only contain digits";
  }
  return true;
});
defineRule("max", (value, [limit]) => {
  if (value && String(value).length > Number(limit)) {
    return `Must be ${limit} characters or less`;
  }
  return true;
});

export default {
  components: { Field, Form, ErrorMessage },
  props: {
    // null/undefined => add mode; an account object => edit mode.
    bankAccount: {
      type: Object,
      default: null,
    },
  },
  emits: ["close-bank-account-modal", "refresh-bank-accounts"],
  data() {
    return {
      showModal: true,
      banks: [
        { label: BANK_LABELS[BANKS.cbe], value: BANKS.cbe },
        { label: BANK_LABELS[BANKS.telebirr], value: BANKS.telebirr },
        { label: BANK_LABELS[BANKS.boa], value: BANKS.boa },
      ],
      bankError: "",
      bankAccountObj: {
        name: null,
        bank: null,
        accountName: null,
        accountNumber: null,
      },
    };
  },
  computed: {
    isEdit() {
      return !!(this.bankAccount && this.bankAccount.id);
    },
    bankAccountIsLoading() {
      return this.$store.getters.bankAccount.isLoading;
    },
  },
  methods: {
    closeModal() {
      this.$emit("close-bank-account-modal");
    },
    saveBankAccount() {
      if (!this.bankAccountObj.bank) {
        this.bankError = "Bank is required";
        return;
      }
      this.bankError = "";

      if (this.isEdit) {
        const payload = {
          id: this.bankAccount.id,
          name: this.bankAccountObj.name || null,
          bank: this.bankAccountObj.bank,
          accountName: this.bankAccountObj.accountName || null,
          accountNumber: this.bankAccountObj.accountNumber,
        };
        this.$store.dispatch(EDIT_BANK_ACCOUNT, payload).then((res) => {
          if (res) {
            this.$toast.success("Bank account updated.", TOAST_GLOBAL_CONFIG);
            this.$emit("refresh-bank-accounts");
            this.closeModal();
          }
        });
      } else {
        const payload = {
          name: this.bankAccountObj.name || null,
          bank: this.bankAccountObj.bank,
          accountName: this.bankAccountObj.accountName || null,
          accountNumber: this.bankAccountObj.accountNumber,
        };
        this.$store.dispatch(REGISTER_BANK_ACCOUNT, payload).then((res) => {
          if (res) {
            this.$toast.success("Bank account added.", TOAST_GLOBAL_CONFIG);
            this.$emit("refresh-bank-accounts");
            this.closeModal();
          }
        });
      }
    },
  },
  mounted() {
    if (this.isEdit) {
      this.bankAccountObj.name = this.bankAccount.name ?? null;
      this.bankAccountObj.bank = this.bankAccount.bank ?? null;
      this.bankAccountObj.accountName = this.bankAccount.accountName ?? null;
      this.bankAccountObj.accountNumber = this.bankAccount.accountNumber ?? null;
    }
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.modal-panel {
  --dashboard-blue: #73c41d;
  --dashboard-blue-dark: #538c13;
  --dashboard-blue-soft: #eef6df;
  --dashboard-blue-border: #cfe6a3;
  --dashboard-slate: #34404a;
  --dashboard-muted: #76828b;
  --dashboard-border: #e3e8e6;
  --dashboard-canvas: #f4f6f3;
  --dashboard-green: #4aa12a;
  --dashboard-red: #e0464d;
  width: 480px;
  max-width: 95vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 1px solid var(--dashboard-border);
  box-shadow: -24px 0 60px rgba(15, 23, 42, 0.18);
  color: var(--dashboard-slate);
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid var(--dashboard-border);
}

.modal-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 5px 11px;
  margin-bottom: 8px;
  border: 1px solid var(--dashboard-border);
  border-radius: var(--ehs-radius-pill);
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--dashboard-green);
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.12);
}

.modal-head h2 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: -0.025em;
}

.modal-head p {
  margin: 4px 0 0;
  color: var(--dashboard-muted);
  font-size: 0.86rem;
}

.icon-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--dashboard-border);
  background: #ffffff;
  border-radius: 50%;
  color: var(--dashboard-slate);
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.icon-button:hover {
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-border);
}

.modal-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 22px 24px 32px;
  background: var(--dashboard-canvas);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-section {
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid var(--dashboard-border);
  border-radius: 16px;
}

.form-section h3 {
  margin: 0 0 14px;
  font-family: var(--ehs-font-display);
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field.full {
  grid-column: 1 / -1;
}

.field > span {
  color: var(--dashboard-muted);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.field > span small {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}

.field input,
.field select {
  padding: 10px 12px;
  border: 1px solid var(--dashboard-border);
  border-radius: 10px;
  background: #ffffff;
  color: var(--dashboard-slate);
  font-size: 0.86rem;
  font-family: inherit;
  outline: none;
  width: 100%;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.field input:focus,
.field select:focus {
  border-color: var(--dashboard-blue);
  box-shadow: 0 0 0 3px rgba(115, 196, 29, 0.18);
}

.form-text.text-danger {
  margin-top: 4px;
  color: var(--dashboard-red);
  font-size: 0.74rem;
  font-weight: 500;
  position: relative;
  left: 4px;
}

.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--dashboard-border);
  background: #ffffff;
}

.ghost-button,
.primary-button {
  border-radius: var(--ehs-radius-pill);
  border: 1px solid transparent;
  padding: 10px 20px;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.ghost-button {
  background: #ffffff;
  color: var(--dashboard-slate);
  border-color: var(--dashboard-border);
}

.ghost-button:hover {
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-border);
}

.primary-button {
  background: var(--dashboard-blue);
  color: #ffffff;
  border-color: var(--dashboard-blue);
}

.primary-button:hover {
  background: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-dark);
}

.primary-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateX(100%);
}

@media (max-width: 991.98px) {
  .modal-panel {
    width: 100%;
    max-width: 100%;
  }

  .modal-head {
    padding: 16px 16px 14px;
    gap: 10px;
  }

  .modal-head h2 {
    font-size: 1.2rem;
  }

  .modal-head p {
    font-size: 0.8rem;
  }

  .modal-body {
    padding: 16px 14px 24px;
  }

  .form-section {
    padding: 14px 14px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .modal-foot {
    padding: 12px 14px;
    flex-direction: column-reverse;
    gap: 8px;
  }

  .modal-foot .ghost-button,
  .modal-foot .primary-button {
    width: 100%;
  }
}
</style>
